import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Paper,
  Pagination,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  Store as StoreIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import DeactivateBusinessDialog from './DeactivateBusinessDialog';

const BusinessManagement = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [businesses, setBusinesses] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [creating, setCreating] = useState(false);
  const [success, setSuccess] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('cards');
  const [searchTerm, setSearchTerm] = useState('');
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [businessToDeactivate, setBusinessToDeactivate] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Constants
  const ITEMS_PER_PAGE = 9;
  const searchTimeoutRef = useRef(null);
  const [formData, setFormData] = useState({
    businessName: '',
    businessType: '',
    businessEmail: '',
    businessPhone: '',
    businessAddress: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: ''
  });

  useEffect(() => {
    fetchBusinesses();
    fetchCategories();
  }, []);

  // Handle search with debouncing
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      if (!searchTerm.trim()) {
        setFilteredBusinesses(businesses);
        return;
      }

      const filtered = businesses.filter(business =>
        business.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        business.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (business.category_name && business.category_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredBusinesses(filtered);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, businesses]);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/super-admin/business-categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      const response = await api.get('/super-admin/businesses');
      const businessData = response.data.businesses || [];
      setBusinesses(businessData);
      setFilteredBusinesses(businessData);
      setError(null);
    } catch (error) {
      console.error('Error fetching businesses:', error);
      setError('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilteredBusinesses(businesses);
  };

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handleBusinessStatusToggle = async (businessId, isCurrentlyActive) => {
    if (isCurrentlyActive) {
      // Find the business and open the deactivate dialog
      const business = businesses.find(b => b.id === businessId);
      setBusinessToDeactivate(business);
      setDeactivateDialogOpen(true);
    } else {
      // Activate directly
      try {
        setActionLoading(true);
        await api.put(`/super-admin/businesses/${businessId}/toggle-status`, {
          reason: null
        });

        // Update local state
        const updatedBusinesses = businesses.map(business =>
          business.id === businessId
            ? { ...business, is_active: true }
            : business
        );
        setBusinesses(updatedBusinesses);
        setFilteredBusinesses(searchTerm.trim() ?
          updatedBusinesses.filter(business =>
            business.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            business.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (business.category_name && business.category_name.toLowerCase().includes(searchTerm.toLowerCase()))
          ) : updatedBusinesses
        );
      } catch (error) {
        setError('Failed to activate business');
      } finally {
        setActionLoading(false);
      }
    }
  };

  const handleDeactivateConfirm = async (reason) => {
    if (!businessToDeactivate) return;

    try {
      setActionLoading(true);
      await api.put(`/super-admin/businesses/${businessToDeactivate.id}/toggle-status`, {
        reason: reason
      });

      // Update local state
      const updatedBusinesses = businesses.map(business =>
        business.id === businessToDeactivate.id
          ? { ...business, is_active: false }
          : business
      );
      setBusinesses(updatedBusinesses);

      // Update filtered businesses if search is active
      if (searchTerm.trim()) {
        const filtered = updatedBusinesses.filter(business =>
          business.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          business.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (business.category_name && business.category_name.toLowerCase().includes(searchTerm.toLowerCase()))
        );
        setFilteredBusinesses(filtered);
      } else {
        setFilteredBusinesses(updatedBusinesses);
      }

      setDeactivateDialogOpen(false);
      setBusinessToDeactivate(null);
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to deactivate business');
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewDetails = (business) => {
    setSelectedBusiness(business);
    setDetailsOpen(true);
  };

  const handleDetailsClose = () => {
    setDetailsOpen(false);
    setSelectedBusiness(null);
  };

  const handleOpenCreateDialog = () => {
    setFormData({
      businessName: '',
      businessType: '',
      businessEmail: '',
      businessPhone: '',
      businessAddress: '',
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: ''
    });
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setError('');
    setSuccess('');
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateBusiness = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.businessName.trim() || !formData.businessType || 
        !formData.businessEmail.trim() || !formData.businessPhone.trim() ||
        !formData.firstName.trim() || !formData.lastName.trim() ||
        !formData.email.trim()) {
      setError('All required fields must be filled');
      return;
    }

    try {
      setCreating(true);
      setError('');

      await api.post('/auth/register', {
        business: {
          name: formData.businessName,
          type: formData.businessType,
          email: formData.businessEmail,
          phone: formData.businessPhone,
          address: formData.businessAddress,
        },
        admin: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          // Super admin creates with a default password that must be changed
          password: 'TempPass123!'
        },
        acceptTerms: true
      });

      setSuccess('Business created successfully! The admin user has been sent verification instructions.');
      setTimeout(() => {
        handleCloseCreateDialog();
        fetchBusinesses(); // Refresh the list
        setCurrentPage(1); // Reset to first page
      }, 2000);

    } catch (error) {
      console.error('Error creating business:', error);
      setError(error.response?.data?.message || 'Failed to create business. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  // Calculate pagination
  const totalPages = Math.ceil(filteredBusinesses.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const displayedBusinesses = filteredBusinesses.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const BusinessCard = ({ business }) => (
    <Card
      elevation={0}
      onClick={() => navigate(`/super-admin/business/${business.id}`)}
      sx={{
        height: 480, // Fixed height
        width: '100%',
        maxWidth: 380, // Fixed max width
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.paper,
        border: 1,
        borderColor: theme.palette.divider,
        borderRadius: 3,
        transition: 'all 0.3s ease-in-out',
        mx: 'auto', // Center the card
        cursor: 'pointer',
        '&:hover': {
          borderColor: theme.palette.primary.main,
          boxShadow: theme.shadows[4],
          transform: 'translateY(-4px)',
        }
      }}
    >
      <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        {/* Header Section */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
          <Box
            sx={{
              p: 1.5,
              bgcolor: theme.palette.primary.main + '15',
              borderRadius: 2,
              mr: 2,
              mt: 0.5
            }}
          >
            <BusinessIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 1,
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                minHeight: '2.6em' // Ensure consistent height for 2 lines
              }}
              title={business.name} // Show full name on hover
            >
              {business.name}
            </Typography>
            <Chip
              size="small"
              label={business.is_active ? 'Active' : 'Inactive'}
              color={business.is_active ? 'success' : 'error'}
              icon={business.is_active ? <CheckCircleIcon /> : <CancelIcon />}
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem'
              }}
            />
          </Box>
        </Box>

        {/* Business Details */}
        <Box sx={{ mb: 3, flexGrow: 1 }}>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Contact
              </Typography>
              <Typography variant="body2" color="textPrimary" sx={{ fontWeight: 500, mt: 0.5 }}>
                {business.email}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Business Type
              </Typography>
              <Typography variant="body2" color="textPrimary" sx={{ fontWeight: 500, mt: 0.5 }}>
                {business.category_name || 'Not specified'}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Address
              </Typography>
              <Typography
                variant="body2"
                color="textPrimary"
                sx={{
                  fontWeight: 500,
                  mt: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: 1.4
                }}
              >
                {business.physical_address || 'Not specified'}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Created
              </Typography>
              <Typography variant="body2" color="textPrimary" sx={{ fontWeight: 500, mt: 0.5 }}>
                {new Date(business.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Stats Section */}
        <Box sx={{
          mb: 3,
          p: 2,
          bgcolor: theme.palette.grey[50],
          borderRadius: 2,
          border: 1,
          borderColor: theme.palette.grey[200]
        }}>
          <Grid container spacing={2}>
            <Grid size={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                  <PeopleIcon sx={{ fontSize: 18, color: theme.palette.primary.main, mr: 0.5 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                    {business.user_count || 0}
                  </Typography>
                </Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                  Users
                </Typography>
              </Box>
            </Grid>
            <Grid size={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                  <StoreIcon sx={{ fontSize: 18, color: theme.palette.secondary.main, mr: 0.5 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.secondary.main }}>
                    {business.branch_count || 0}
                  </Typography>
                </Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                  Branches
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Action Buttons */}
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => handleViewDetails(business)}
            sx={{
              textTransform: 'none',
              flexGrow: 1,
              py: 1,
              fontWeight: 600,
              borderWidth: 2,
              '&:hover': {
                borderWidth: 2,
              }
            }}
          >
            Details
          </Button>
          <Button
            size="small"
            variant={business.is_active ? "outlined" : "contained"}
            color={business.is_active ? "error" : "success"}
            startIcon={business.is_active ? <CancelIcon /> : <CheckCircleIcon />}
            onClick={() => handleBusinessStatusToggle(business.id, business.is_active)}
            sx={{
              textTransform: 'none',
              flexGrow: 1,
              py: 1,
              fontWeight: 600,
              borderWidth: business.is_active ? 2 : 0,
              '&:hover': {
                borderWidth: business.is_active ? 2 : 0,
              }
            }}
          >
            {business.is_active ? 'Deactivate' : 'Activate'}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );

  const BusinessListView = () => (
    <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: theme.palette.divider, borderRadius: 2 }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.grey[50] }}>Business</TableCell>
            <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.grey[50] }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.grey[50] }}>Contact</TableCell>
            <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.grey[50] }}>Address</TableCell>
            <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.grey[50] }}>Stats</TableCell>
            <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.grey[50] }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.grey[50] }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {displayedBusinesses.map((business) => (
            <TableRow
              key={business.id}
              hover
              onClick={() => navigate(`/super-admin/business/${business.id}`)}
              sx={{ cursor: 'pointer', '&:hover': { backgroundColor: theme.palette.action.hover } }}
            >
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={{
                      p: 1,
                      bgcolor: theme.palette.primary.main + '15',
                      borderRadius: 1,
                      mr: 2
                    }}
                  >
                    <BusinessIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {business.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Created: {new Date(business.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </Typography>
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {business.category_name || 'Not specified'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {business.email}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  sx={{
                    maxWidth: 150,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  title={business.physical_address}
                >
                  {business.physical_address || 'Not specified'}
                </Typography>
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PeopleIcon sx={{ fontSize: 16, color: theme.palette.primary.main, mr: 0.5 }} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {business.user_count || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <StoreIcon sx={{ fontSize: 16, color: theme.palette.secondary.main, mr: 0.5 }} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {business.branch_count || 0}
                    </Typography>
                  </Box>
                </Stack>
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={business.is_active ? 'Active' : 'Inactive'}
                  color={business.is_active ? 'success' : 'error'}
                  icon={business.is_active ? <CheckCircleIcon /> : <CancelIcon />}
                  sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                />
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                    onClick={(e) => { e.stopPropagation(); handleViewDetails(business); }}
                    sx={{ textTransform: 'none', minWidth: 'auto', px: 1 }}
                  >
                    View
                  </Button>
                  <Button
                    size="small"
                    variant={business.is_active ? "outlined" : "contained"}
                    color={business.is_active ? "error" : "success"}
                    startIcon={business.is_active ? <CancelIcon /> : <CheckCircleIcon />}
                    onClick={(e) => { e.stopPropagation(); handleBusinessStatusToggle(business.id, business.is_active); }}
                    sx={{ textTransform: 'none', minWidth: 'auto', px: 1 }}
                  >
                    {business.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Business Management
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
            sx={{ textTransform: 'none' }}
          >
            Add Business
          </Button>
          <Button
            variant="outlined"
            onClick={fetchBusinesses}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {/* Search and Controls */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              placeholder="Search businesses by name, email, type, or address..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClearSearch} size="small">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>
                {filteredBusinesses.length} business{filteredBusinesses.length !== 1 ? 'es' : ''}
                {searchTerm && ` found for "${searchTerm}"`}
              </Typography>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    borderRadius: 1,
                    px: 2,
                    py: 0.5
                  }
                }}
              >
                <ToggleButton value="cards" aria-label="card view">
                  <ViewModuleIcon sx={{ mr: 0.5 }} />
                  Cards
                </ToggleButton>
                <ToggleButton value="list" aria-label="list view">
                  <ViewListIcon sx={{ mr: 0.5 }} />
                  List
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Content Area */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : businesses.length === 0 ? (
        <Alert severity="info">
          No businesses found in the system.
        </Alert>
      ) : filteredBusinesses.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <SearchIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" sx={{ mb: 1 }}>
            No businesses found
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Try adjusting your search terms or clear the search to see all businesses.
          </Typography>
          <Button
            variant="outlined"
            onClick={handleClearSearch}
            sx={{ mt: 2, textTransform: 'none' }}
          >
            Clear Search
          </Button>
        </Box>
      ) : (
        <>
          {/* Business Display */}
          {viewMode === 'cards' ? (
            <Grid container spacing={3} sx={{ justifyContent: 'flex-start' }}>
              {displayedBusinesses.map((business) => (
                <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={business.id} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <BusinessCard business={business} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <BusinessListView />
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Box>
          )}
        </>
      )}

      {/* Business Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={handleDetailsClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            m: isMobile ? 1 : 3
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Business Details
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedBusiness && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Business Name"
                  value={selectedBusiness.name}
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Email"
                  value={selectedBusiness.email}
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={selectedBusiness.phone || 'N/A'}
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Business Type"
                  value={selectedBusiness.category_name || 'N/A'}
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={selectedBusiness.physical_address || 'N/A'}
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Status"
                  value={selectedBusiness.status}
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Created At"
                  value={new Date(selectedBusiness.created_at).toLocaleString()}
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={handleDetailsClose} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Business Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={handleCloseCreateDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            m: isMobile ? 1 : 2
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Create New Business
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Set up a new business with an admin account
          </Typography>
        </DialogTitle>
        
        <form onSubmit={handleCreateBusiness}>
          <DialogContent>
            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {success}
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                mb: 3, 
                border: 1, 
                borderColor: 'divider',
                borderRadius: 2
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Business Information
              </Typography>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <TextField
                    name="businessName"
                    label="Business Name"
                    value={formData.businessName}
                    onChange={handleFormChange}
                    fullWidth
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid size={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Business Category</InputLabel>
                    <Select
                      name="businessType"
                      value={formData.businessType}
                      onChange={handleFormChange}
                      label="Business Category"
                    >
                      {categories.map((category) => (
                        <MenuItem key={category.code} value={category.code}>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {category.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              {category.description}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="businessEmail"
                    label="Business Email"
                    type="email"
                    value={formData.businessEmail}
                    onChange={handleFormChange}
                    fullWidth
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="businessPhone"
                    label="Business Phone"
                    value={formData.businessPhone}
                    onChange={handleFormChange}
                    fullWidth
                    required
                    variant="outlined"
                    helperText="Use Kenyan format: +254712345678"
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    name="businessAddress"
                    label="Business Address"
                    value={formData.businessAddress}
                    onChange={handleFormChange}
                    fullWidth
                    multiline
                    rows={2}
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                border: 1, 
                borderColor: 'divider',
                borderRadius: 2
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Admin Account
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="firstName"
                    label="First Name"
                    value={formData.firstName}
                    onChange={handleFormChange}
                    fullWidth
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="lastName"
                    label="Last Name"
                    value={formData.lastName}
                    onChange={handleFormChange}
                    fullWidth
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="email"
                    label="Admin Email"
                    type="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    fullWidth
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="phoneNumber"
                    label="Admin Phone"
                    value={formData.phoneNumber}
                    onChange={handleFormChange}
                    fullWidth
                    variant="outlined"
                    helperText="Use Kenyan format: +254712345678"
                  />
                </Grid>
                <Grid size={12}>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    The admin account will be created with a temporary password and will receive email verification instructions.
                  </Alert>
                </Grid>
              </Grid>
            </Paper>
          </DialogContent>
          
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button 
              onClick={handleCloseCreateDialog} 
              disabled={creating}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={creating}
              startIcon={creating ? <CircularProgress size={16} /> : <AddIcon />}
              sx={{ textTransform: 'none' }}
            >
              {creating ? 'Creating Business...' : 'Create Business'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Deactivate Business Dialog */}
      <DeactivateBusinessDialog
        open={deactivateDialogOpen}
        onClose={() => {
          setDeactivateDialogOpen(false);
          setBusinessToDeactivate(null);
        }}
        onConfirm={handleDeactivateConfirm}
        businessName={businessToDeactivate?.name || ''}
        loading={actionLoading}
      />
    </Box>
  );
};

export default BusinessManagement;